// src/core/data-tables/DataTable.tsx
import React, {
  useState,
  useRef,
  useLayoutEffect,
  useMemo,
  useCallback,
} from "react";
import {
  Table,
  TableHead,
  TableHeader,
  TableRow,
  TableCell,
  TableCaption,
} from "@ui/table";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Clipboard,
  Check as CheckIcon,
} from "lucide-react";
import { formatValue } from "./format";
import { improveColumnName, calcTableBodyHeight } from "./table-utils";
import { useColumnResize } from "./useColumnResize";
import { useSort } from "./useSort";
import { useClipboardFlash } from "./useClipboardFlash";
import { getTheme } from "./theme";
import { cn } from "@/lib";
import { useColumnTypes } from "./useColumnTypes";

export interface Action<T> {
  header: string;
  width?: number;
  render: (row: T, rowIndex: number) => React.ReactNode;
}

interface Props {
  data: any[];
  columns: string[];
  /** DuckDB-declared type for each column (e.g. "DATE", "TIMESTAMP") */
  declaredTypes?: Record<string, string>;
  caption?: string;
  maxHeight?: string;
  enableSorting?: boolean;
  improveColumnNames?: boolean;
  colorScheme?: "default" | "blue" | "gray" | "green";
  rowHeight?: number;
  enableRowCopy?: boolean;
  actions?: Action<any>[];
}

export default function DataTable({
  data,
  columns,
  declaredTypes = {},
  caption,
  maxHeight = "700px",
  enableSorting = true,
  improveColumnNames = false,
  colorScheme = "default",
  rowHeight = 36,
  enableRowCopy = false,
  actions = [],
}: Props) {
  // build a single DataType per column for sorting/etc
  useColumnTypes(data, columns);

  /* column-resize */
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { columnWidths, resetColumnWidths, handleResizeStart } =
    useColumnResize(columns, wrapperRef, 160);

  const [wrapperW, setWrapperW] = useState(0);
  useLayoutEffect(() => {
    if (!wrapperRef.current) return;
    const ro = new ResizeObserver(() =>
      setWrapperW(
        wrapperRef.current!.clientWidth - (enableRowCopy ? 36 : 0) - 1,
      ),
    );
    ro.observe(wrapperRef.current);
    return () => ro.disconnect();
  }, [enableRowCopy]);

  const scaledWidths = useMemo(() => {
    const total =
      columns.reduce((sum, c) => sum + (columnWidths[c] ?? 160), 0) +
      (enableRowCopy ? 36 : 0);
    if (total >= wrapperW || !wrapperW) return columnWidths;
    const f = wrapperW / total;
    return Object.fromEntries(
      columns.map((c) => [
        c,
        Math.max(100, Math.floor((columnWidths[c] ?? 160) * f)),
      ]),
    );
  }, [columnWidths, columns, wrapperW, enableRowCopy]);

  /* sorting + clipboard */
  const { sortedData, sortConfig, toggleSort } = useSort(data, enableSorting);
  const { copiedRow, copyText } = useClipboardFlash();

  /* tooltip */
  const [tip, setTip] = useState<null | { text: string; x: number; y: number }>(
    null,
  );
  const showTip = useCallback(
    (label: string, e: React.MouseEvent) => {
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
      setTip({ text: label, x: rect.left + rect.width / 2, y: rect.top });
    },
    [],
  );
  const hideTip = useCallback(() => setTip(null), []);

  /* theming + layout */
  const scheme = getTheme(colorScheme);
  const bodyHeight = calcTableBodyHeight(maxHeight);

  return (
    <div
      ref={wrapperRef}
      className={cn(
        "relative data-table-container rounded-lg border border-gray-200 dark:border-gray-700",
        "bg-white dark:bg-gray-900 shadow-sm antialiased",
      )}
    >
      {/* utility bar */}
      <div className="flex justify-end p-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
        <button
          className="text-xs px-2 py-1 rounded bg-muted hover:bg-muted/80 dark:bg-gray-700 dark:hover:bg-gray-600"
          onClick={resetColumnWidths}
        >
          ↻ Reset columns
        </button>
      </div>

      {/* scroll viewport */}
      <div
        style={{ maxHeight: bodyHeight, overflow: "auto" }}
        className="custom-scrollbar"
      >
        <Table className="w-full text-sm antialiased" style={{ tableLayout: "fixed" }}>
          {caption && <TableCaption>{caption}</TableCaption>}

          {/* header */}
          <TableHeader>
            <TableRow>
              {enableRowCopy && <TableHead style={{ width: 36 }} />}
              {columns.map((col) => {
                const w = scaledWidths[col];
                const label = improveColumnNames ? improveColumnName(col) : col;
                return (
                  <TableHead
                    key={col}
                    style={{
                      position: "sticky",
                      top: 0,
                      zIndex: 20,
                      width: w,
                      minWidth: w,
                      maxWidth: w,
                    }}
                    className={cn(
                      scheme.header,
                      enableSorting && "cursor-pointer select-none",
                      "overflow-visible",
                    )}
                    onClick={() => enableSorting && toggleSort(col)}
                    onMouseDown={(e) => handleResizeStart(col, e)}
                    onMouseEnter={(e) => showTip(label, e)}
                    onMouseLeave={hideTip}
                  >
                    <div className="flex items-center">
                      <span className="truncate text-lg font-semibold">{label}</span>
                      {enableSorting &&
                        (sortConfig.col !== col ? (
                          <ArrowUpDown className="h-3.5 w-3.5 ml-1 opacity-50" />
                        ) : sortConfig.dir === "asc" ? (
                          <ArrowUp className="h-3.5 w-3.5 ml-1 text-brand" />
                        ) : (
                          <ArrowDown className="h-3.5 w-3.5 ml-1 text-brand" />
                        ))}
                    </div>
                  </TableHead>
                );
              })}
              {actions.map((act, i) => (
                <TableHead
                  key={`action-${i}`}
                  style={{
                    position: "sticky",
                    top: 0,
                    zIndex: 20,
                    width: act.width,
                    minWidth: act.width,
                    maxWidth: act.width,
                  }}
                  className={cn(scheme.header, "text-center")}
                >
                  <span className="text-lg font-semibold">{act.header}</span>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>

          {/* body */}
          <tbody>
            {sortedData.map((row, rIdx) => (
              <TableRow
                key={rIdx}
                className={cn(rIdx % 2 ? scheme.alt : "", scheme.row)}
                style={{ height: rowHeight }}
              >
                {enableRowCopy && (
                  <TableCell
                    className="p-0"
                    style={{ width: 36, minWidth: 36, maxWidth: 36 }}
                  >
                    <button
                      onDoubleClick={() =>
                        copyText([...columns.map((c) => row[c])].join("\t"), rIdx)
                      }
                      title="Copy this row"
                      className={cn(
                        "copy-row-btn w-full h-full",
                        copiedRow === rIdx && "bg-brand text-white",
                      )}
                    >
                      {copiedRow === rIdx ? <CheckIcon size={15} /> : <Clipboard size={15} />}
                    </button>
                  </TableCell>
                )}

                {columns.map((c) => {
                  const w = scaledWidths[c];
                  const duckType = declaredTypes[c];
                  return (
                    <TableCell
                      key={`${rIdx}-${c}`}
                      style={{ width: w, minWidth: w, maxWidth: w }}
                      onMouseDown={(e) => handleResizeStart(c, e)}
                      onDoubleClick={() => copyText(String(row[c]), rIdx)}
                    >
                      <div className="truncate">
                        {formatValue(row[c], c, undefined, duckType)}
                      </div>
                    </TableCell>
                  );
                })}

                {actions.map((act, i) => (
                  <TableCell
                    key={`action-cell-${rIdx}-${i}`}
                    style={{
                      width: act.width,
                      minWidth: act.width,
                      maxWidth: act.width,
                    }}
                  >
                    {act.render(row, rIdx)}
                  </TableCell>
                ))}
              </TableRow>
            ))}

            {!sortedData.length && (
              <TableRow>
                <TableCell
                  colSpan={columns.length + (enableRowCopy ? 1 : 0) + actions.length}
                  className="h-24 text-center text-muted-foreground"
                >
                  No data available
                </TableCell>
              </TableRow>
            )}
          </tbody>
        </Table>
      </div>

      {/* floating tooltip */}
      {tip && (
        <div
          className="fixed px-3 py-1.5 bg-background border rounded shadow-lg text-xl font-semibold whitespace-nowrap pointer-events-none z-[100]"
          style={{
            top: tip.y - 8,
            left: tip.x,
            transform: "translate(-50%, -100%)",
          }}
        >
          {tip.text}
        </div>
      )}
    </div>
  );
}
